import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, Loader2, Mail, ArrowLeft, CheckCircle2, ExternalLink } from "lucide-react";
import { forgotPassword } from "@/lib/api";

/**
 * Forgot-password page. Always shows the same "check your email" screen so an
 * attacker can't enumerate registered accounts. In dry-run email mode the
 * backend also returns `dev_link` so testers can complete the flow without SMTP.
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || loading) return;
    setLoading(true);
    try {
      const res = await forgotPassword(email.trim());
      setSent(true);
      setDevLink(res.dev_link || "");
    } catch {
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grain min-h-screen grid place-items-center p-6 bg-background">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md rounded-3xl border border-border bg-card p-8 shadow-lg"
        data-testid="forgot-password-page"
      >
        <Link to="/login" data-testid="back-to-login" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>

        <div className="mt-6 flex items-center gap-2.5">
          <span className="grid place-items-center h-11 w-11 rounded-2xl bg-accent/15 text-accent">
            <BookOpen className="h-6 w-6" />
          </span>
          <span className="font-serif text-2xl font-bold">Lectura<span className="text-accent">.</span></span>
        </div>

        {sent ? (
          <div className="mt-6" data-testid="forgot-sent-state">
            <span className="grid place-items-center h-14 w-14 rounded-2xl bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
              <CheckCircle2 className="h-7 w-7" />
            </span>
            <h1 className="mt-4 font-serif text-2xl font-bold">Check your email</h1>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              If <span className="font-semibold text-foreground">{email}</span> is registered, we sent a password reset link. Open it within the next hour to choose a new password.
            </p>

            {devLink && (
              <div className="mt-5 rounded-2xl border-2 border-dashed border-amber-500/50 bg-amber-500/10 p-4" data-testid="dev-reset-link">
                <p className="text-[10px] font-bold uppercase tracking-widest text-amber-800 dark:text-amber-300">
                  Dev-mode fallback (emails aren't wired to Resend yet)
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Use this link directly to test the flow:
                </p>
                <a
                  href={devLink}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-mono font-semibold text-accent break-all hover:underline"
                >
                  {devLink} <ExternalLink className="h-3 w-3 shrink-0" />
                </a>
              </div>
            )}

            <div className="mt-6 flex flex-col gap-2">
              <button
                onClick={() => { setSent(false); setDevLink(""); }}
                data-testid="forgot-try-again"
                className="text-sm font-semibold text-accent hover:underline"
              >
                Try a different email
              </button>
              <button
                onClick={() => navigate("/login")}
                className="text-sm text-muted-foreground hover:text-foreground transition"
              >
                Back to sign in
              </button>
            </div>
          </div>
        ) : (
          <>
            <h1 className="mt-6 font-serif text-2xl font-bold">Forgot your password?</h1>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              Enter the email you used to sign up. We'll send a reset link — it works for one hour.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  data-testid="forgot-email-input"
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@school.edu"
                  className="w-full rounded-xl border border-input bg-background pl-11 pr-4 py-3 outline-none focus:ring-2 focus:ring-ring/60"
                />
              </div>
              <button
                data-testid="forgot-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-accent text-accent-foreground py-3.5 font-semibold disabled:opacity-60 hover:opacity-90 transition"
              >
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : "Send reset link"}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
